import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
//import ErrorView from '../components/shared/ErrorView';
import NotFoundPage from "../components/NotFoundPage";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import ProtectedRoute from "../components/shared/ProtectedRoute";
import AppInitializer from "../config/AppInitializer";
import { SharedBlipPage } from "@/pages/SharedBlipPage";
import LivestreamRedirectPage from "@/pages/LivestreamRedirectPage";
const InviteRedirectPage = lazy(() => import("@/pages/InviteRedirectPage"));



import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCartStore } from '@/stores/cartStore';
import WishlistPage from "@/components/shop/WishlistPage";
import OrderSuccessPage from "@/pages/OrdersSuccessPage";
import SavedBlipsPage from "@/pages/saved-blips-page";
const WalletPage = lazy(() => import("../components/wallet/WalletPage"));
const TransactionsHistoryPage = lazy(() => import("../components/wallet/TransactionsHistoryPage"));




interface ProtectedCheckoutProps {
  children: React.ReactNode;
}

export function ProtectedCheckout({ children }: ProtectedCheckoutProps) {
  const { user, isLoading } = useAuth();
  const { items } = useCartStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-green"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login with return URL
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location, message: 'Please login to complete your purchase' }}
        replace
      />
    );
  }

  // If cart is empty, redirect to shop
  if (items.length === 0) {
    return <Navigate to="/shop" replace />;
  }

  return <>{children}</>;
}


// Lazy load components for better performance
const ProductDetailsPage = lazy(
  () => import("../components/products/ProductDetailsPage")
);
const BlipViewPage = lazy(() => import("../components/blips/BlipViewPage"));
const BlipFeedPage = lazy(() => import("../components/blips/BlipFeedPage"));
const StreamView = lazy(() => import("../components/StreamView"));
const HomePage = lazy(() => import("../pages/HomePage"));
const ShopPage = lazy(() => import("../pages/ShopPage"));
const LivePage = lazy(() => import("../pages/LivePage"));

const SignUpPage = lazy(() => import("../components/auth/signup/SignUpPage"));
const LoginPageContainer = lazy(
  () => import("../components/auth/login/LoginPageContainer")
);
const ForgotPasswordPage = lazy(
  () => import("../components/auth/forgot-password/ForgotPasswordPage")
);
const CheckoutPage = lazy(() => import("../components/checkout/CheckoutPage"));
const FilteredSubcategoryPage  = lazy(() => import("../pages/FilteredSubcategoryPage "));
const SearchResultsPage = lazy(() => import("../components/search/SearchResultsPage"));
const BuyerProfilePage = lazy(() => import("../components/profile/BuyerProfilePage"));
const SellerProfilePage = lazy(
  () => import("../components/profile/SellerProfilePage")
);

const FilteredCategoryPage = lazy(()=> import("../pages/FilteredCategoryPage") )
const CategoriesPage = lazy(() => import("../pages/CategoriesPage"));
const FollowingPage = lazy(() => import("../pages/FollowingPage"));

// Route type definition
type RouteConfig = {
  path: string;
  element: React.ReactElement;
  title: string;
  roles?: string[]; // Future RBAC support
};

// Public routes - no authentication required
const publicRoutes: RouteConfig[] = [
  {
    path: "/",
    element: <HomePage />,
    title: "Home",
  },
  {
    path: "/shop",
    element: <ShopPage />,
    title: "Shop",
  },
  {
    path: "/live",
    element: <LivePage />,
    title: "Live",
  },
  {
    path: "/signup",
    element: <SignUpPage />,
    title: "Sign Up",
  },
  {
    path: "/login",
    element: <LoginPageContainer />,
    title: "Login",
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
    title: "Forgot Password",
  },
  {
    path: "/products/:id",
    element: <ProductDetailsPage />,
    title: "Product",
  },
  {
    path: "/products/:productId/blips/:blipId",
    element: <BlipViewPage />,
    title: "Blip",
  },
  {
    path: "/blips",
    element: <BlipFeedPage />,
    title: "Blips Feed",
  },
  {
    path: "/stream/:id",
    element: <StreamView />,
    title: "Stream",
  },
  {
    path: "/seller/:id",
    element: <SellerProfilePage />,
    title: "Seller Profile",
  },
  {
    path: "/share/blips/:blipId",
    element: <SharedBlipPage />,
    title: "Shared Blip",
  },
  {
    path: "/checkout",
    element: <CheckoutPage />,
    title: "Checkout",
  },
  {
    path: "/category/:category/subcategory/:subcategory",
    element: <FilteredSubcategoryPage />,
    title: "Subcategory Products",
  },
  {
    path: "/category/:categoryName",
    element: <FilteredCategoryPage />,
    title: "Category Products",
  },
  {
    path: "/categories",
    element: <CategoriesPage />,
    title: "All Categories",
  },
  {
    path: "/search",
    element: <SearchResultsPage />,
    title: "Search Results",
  },
  {
    path: "/profile/:sellerId",
    element: <SellerProfilePage />,
    title: "Buyer Profile",
  },
  {
    path: "/profile",
    element: <BuyerProfilePage />,
    title: "My Profile",
  },
  {
    path: "/wishlist",
    element: <WishlistPage />,
    title: "My Wishlist",
  },
  {
    path: "/orders-success",
    element: <OrderSuccessPage />,
    title: "My Wishlist",
  },
  {
    path: "/saved-blips",
    element: <SavedBlipsPage/>,
    title: "Saved Blips",
  },
  {
    path: "/following",
    element: <FollowingPage />,
    title: "Following",
  },
  {
    path: "/wallet",
    element: <WalletPage />,
    title: "My Wallet",
  },
  {
    path: "/wallet/transactions",
    element: <TransactionsHistoryPage />,
    title: "Transactions History",
  },
  
  {
    path: "/livestream/feed",
    element: <LivestreamRedirectPage />,
    title: "Livestream",
  },
  {
    path: "/invite",
    element: <InviteRedirectPage />,
    title: "Invite",
  },

];

// Protected routes - require authentication
// Future routes will be added here
const protectedRoutes: RouteConfig[] = [
  // Example of future protected routes:
  // {
  //   path: '/profile',
  //   element: <ProfilePage />,
  //   title: 'Profile',
  //   roles: ['user', 'admin'], // Future RBAC support
  // },
  {
    path: '/checkout',
    element: <ProtectedCheckout>
      <CheckoutPage />
    </ProtectedCheckout>,
    title: 'Checkout'
  }
];

// Helper function to wrap protected routes with ProtectedRoute component
const wrapProtectedRoute = (element: React.ReactElement) => (
  <ProtectedRoute>{element}</ProtectedRoute>
);

/**
 * Main routing component
 *
 * This component handles all routing logic in a centralized way.
 * Benefits:
 * - Easy to add new routes
 * - Clear separation of public vs protected routes
 * - Automatic protection for authenticated routes
 * - Lazy loading for better performance
 * - Consistent loading states
 * - Future-proof for role-based access control
 */
const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <AppInitializer />
      <Routes>
        {/* Public routes - no authentication required */}
        {publicRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}

        {/* Protected routes - automatically wrapped with ProtectedRoute */}
        {protectedRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={wrapProtectedRoute(element)} />
        ))}

        {/* Catch-all route for 404 errors */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
